import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { partnerTicketAdminAPI, superAdminAPI, channelPartnerAdminAPI } from '../../services/api';
import InlineAttachments from '../../components/Support/InlineAttachments';
import { useSuperAdmin } from '../../contexts/SuperAdminContext';
import { ExportMenu, DateRangeFilter, FilterSelect, ResetFiltersButton } from '../../components/SuperAdmin/ListTools';
import { fetchAllPages, dateRangeParams, formatExportDate } from '../../utils/exportData';
import toast from 'react-hot-toast';
import {
  HiOutlineTicket, HiOutlineSearch, HiOutlineX, HiOutlineRefresh,
  HiOutlineExclamationCircle, HiOutlineCheckCircle, HiOutlineClock,
  HiOutlineChevronRight, HiOutlinePaperAirplane, HiOutlinePaperClip,
  HiOutlinePlus, HiOutlinePencil, HiOutlineTrash, HiOutlineArrowUp, HiOutlineArrowDown,
} from 'react-icons/hi';

const PRIORITY_STYLES = {
  low: 'bg-gray-100 text-gray-700',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-amber-100 text-amber-700',
  urgent: 'bg-red-100 text-red-700',
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const initials = (name) => (name || '?').split(' ').map((p) => p.charAt(0)).slice(0, 2).join('').toUpperCase();

const Spinner = () => <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />;

// ─── Ticket Detail Drawer ───────────────────────────────────────────────────

const TicketDetailDrawer = ({ ticketId, onClose }) => {
  const queryClient = useQueryClient();
  const { superAdmin } = useSuperAdmin();
  const [newStageId, setNewStageId] = useState('');
  const [newPriority, setNewPriority] = useState('');
  const [newAssigneeIds, setNewAssigneeIds] = useState([]);
  const [stageReason, setStageReason] = useState('');
  const [replyText, setReplyText] = useState('');
  const [replyInternal, setReplyInternal] = useState(false);
  const [replyFiles, setReplyFiles] = useState([]);

  const { data, isLoading } = useQuery({
    queryKey: ['superadmin-partner-ticket', ticketId],
    queryFn: async () => (await partnerTicketAdminAPI.getTicket(ticketId)).data,
    enabled: !!ticketId,
  });

  // Same shared SuperAdmin staff pool used by org tickets' assignee dropdown.
  const adminsQuery = useQuery({
    queryKey: ['superadmin-assignable-admins'],
    queryFn: async () => (await superAdminAPI.getAssignableSupportAdmins()).data,
  });
  const supportAdmins = adminsQuery.data?.data || [];

  const ticket = data?.data?.ticket;
  const stages = data?.data?.stages || [];

  React.useEffect(() => {
    if (ticket) {
      setNewStageId(ticket.stage?._id || '');
      setNewPriority(ticket.priority || 'medium');
      setNewAssigneeIds((ticket.assignedSuperAdminIds || []).map((a) => a._id));
    }
  }, [ticket?._id]);

  const toggleAssignee = (adminId) => {
    setNewAssigneeIds((prev) => (prev.includes(adminId) ? prev.filter((id) => id !== adminId) : [...prev, adminId]));
  };

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['superadmin-partner-ticket', ticketId] });
    queryClient.invalidateQueries({ queryKey: ['superadmin-partner-tickets'] });
    queryClient.invalidateQueries({ queryKey: ['superadmin-partner-ticket-stats'] });
  };

  const updateMutation = useMutation({
    mutationFn: (payload) => partnerTicketAdminAPI.updateTicket(ticketId, payload),
    onSuccess: (res) => {
      invalidate();
      toast.success(res.data?.message || 'Ticket updated');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to update ticket'),
  });

  const replyMutation = useMutation({
    mutationFn: () => {
      if (replyFiles.length === 0) {
        return partnerTicketAdminAPI.followupTicket(ticketId, { message: replyText.trim(), visibleToCustomer: !replyInternal });
      }
      const formData = new FormData();
      formData.append('message', replyText.trim());
      formData.append('visibleToCustomer', String(!replyInternal));
      replyFiles.forEach((f) => formData.append('files', f));
      return partnerTicketAdminAPI.followupTicketWithFiles(ticketId, formData);
    },
    onSuccess: () => {
      invalidate();
      setReplyText('');
      setReplyFiles([]);
      setReplyInternal(false);
      toast.success('Reply sent');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to send reply'),
  });

  const handleSaveChanges = () => {
    const payload = {};
    if (newStageId && newStageId !== (ticket.stage?._id || '')) {
      payload.stageId = newStageId;
      payload.reason = stageReason.trim() || undefined;
    }
    if (newPriority && newPriority !== ticket.priority) payload.priority = newPriority;
    const currentIds = (ticket.assignedSuperAdminIds || []).map((a) => a._id).sort().join(',');
    const nextIds = [...newAssigneeIds].sort().join(',');
    if (currentIds !== nextIds) payload.assigneeIds = newAssigneeIds;
    if (Object.keys(payload).length === 0) return toast('Nothing changed');
    updateMutation.mutate(payload);
  };

  const canReassign = superAdmin?.role === 'super_admin';

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="font-semibold text-gray-900">Ticket Detail</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100">
            <HiOutlineX className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {isLoading || !ticket ? (
          <div className="flex items-center justify-center py-16"><Spinner /></div>
        ) : (
          <div className="p-5 space-y-5">
            <div>
              <p className="text-xs font-mono text-gray-400">{ticket.ticketNumber}</p>
              <h3 className="text-lg font-semibold text-gray-900 mt-0.5">{ticket.subject}</h3>
              <p className="text-sm text-gray-600 mt-1.5">{ticket.description}</p>
              <InlineAttachments attachments={ticket.attachments} />
              <div className="flex items-center gap-2 text-xs text-gray-500 mt-2">
                <span>{ticket.channelPartnerId?.name || '—'}</span>
                <span className="text-gray-300">•</span>
                <span>{ticket.customerEmail}</span>
                <span className="text-gray-300">•</span>
                <span>{formatDate(ticket.createdAt)}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Stage</label>
                <select value={newStageId} onChange={(e) => setNewStageId(e.target.value)} className="w-full border border-gray-300 rounded-lg px-2.5 py-2 text-sm">
                  {stages.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Priority</label>
                <select value={newPriority} onChange={(e) => setNewPriority(e.target.value)} className="w-full border border-gray-300 rounded-lg px-2.5 py-2 text-sm">
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>
            {newStageId !== (ticket.stage?._id || '') && (
              <input
                type="text"
                value={stageReason}
                onChange={(e) => setStageReason(e.target.value)}
                placeholder="Reason for stage change (optional)"
                className="w-full border border-gray-300 rounded-lg px-2.5 py-2 text-sm"
              />
            )}

            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">
                Assigned Staff {!canReassign && <span className="font-normal">(only a Super Admin can change this)</span>}
              </label>
              <div className="flex flex-wrap gap-1.5">
                {supportAdmins.map((a) => (
                  <button
                    key={a._id}
                    type="button"
                    disabled={!canReassign}
                    onClick={() => toggleAssignee(a._id)}
                    className={`text-xs px-2.5 py-1 rounded-lg border ${newAssigneeIds.includes(a._id) ? 'bg-primary-50 border-primary-300 text-primary-700' : 'bg-white border-gray-300 text-gray-600'} ${!canReassign ? 'opacity-60 cursor-not-allowed' : ''}`}
                  >
                    {a.name}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={handleSaveChanges}
              disabled={updateMutation.isPending}
              className="w-full bg-primary-600 text-white rounded-lg py-2 text-sm font-medium hover:bg-primary-700 disabled:opacity-60"
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>

            <div className="border-t border-gray-200 pt-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Conversation</h4>
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {ticket.activities.map((a, i) => (
                  <div key={a._id || i} className="text-sm">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-800">{a.createdByName}</span>
                      <span className="text-xs text-gray-400">{formatDate(a.createdAt)}</span>
                      {a.visibleToCustomer === false && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Internal</span>}
                    </div>
                    <p className="text-gray-600 mt-0.5">{a.message}</p>
                    <InlineAttachments attachments={a.attachments} />
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-gray-200 pt-4 space-y-2">
              <textarea
                rows={3}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Type a reply..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                    <input type="checkbox" checked={replyInternal} onChange={(e) => setReplyInternal(e.target.checked)} />
                    Internal note (not visible to partner)
                  </label>
                  <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                    <HiOutlinePaperClip className="w-4 h-4" />
                    <input type="file" multiple className="hidden" onChange={(e) => setReplyFiles(Array.from(e.target.files || []))} />
                    {replyFiles.length > 0 ? `${replyFiles.length} file(s)` : 'Attach'}
                  </label>
                </div>
                <button
                  onClick={() => replyMutation.mutate()}
                  disabled={replyMutation.isPending || !replyText.trim()}
                  className="flex items-center gap-1.5 bg-primary-600 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-primary-700 disabled:opacity-60"
                >
                  <HiOutlinePaperAirplane className="w-4 h-4" />
                  {replyMutation.isPending ? 'Sending...' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Tickets Panel ──────────────────────────────────────────────────────────

const EMPTY_PARTNER_TICKET_FILTERS = {
  search: '', priority: '', stage: '', category: '', state: '', assignee: '', channelPartnerId: '', from: '', to: '',
};

const partnerTicketApiParams = ({ from, to, ...rest }) => ({ ...rest, ...dateRangeParams(from, to) });

const PARTNER_TICKET_EXPORT_COLUMNS = [
  { header: 'Ticket #', value: (t) => t.ticketNumber },
  { header: 'Subject', value: (t) => t.subject },
  { header: 'Channel Partner', value: (t) => t.channelPartnerId?.name },
  { header: 'Partner Email', value: (t) => t.channelPartnerId?.email },
  { header: 'Customer Name', value: (t) => t.customerName },
  { header: 'Customer Email', value: (t) => t.customerEmail },
  { header: 'Customer Phone', value: (t) => t.customerPhone },
  { header: 'Category', value: (t) => t.category?.name },
  { header: 'Priority', value: (t) => t.priority },
  { header: 'Stage', value: (t) => t.stage?.name },
  { header: 'State', value: (t) => (t.closedAt ? 'Closed' : t.resolvedAt ? 'Resolved' : 'Open') },
  { header: 'Assigned Support', value: (t) => (t.assignedSuperAdminIds || []).map((a) => a?.name) },
  { header: 'First Response', value: (t) => formatExportDate(t.firstResponseAt) },
  { header: 'Resolved', value: (t) => formatExportDate(t.resolvedAt) },
  { header: 'Closed', value: (t) => formatExportDate(t.closedAt) },
  { header: 'Created', value: (t) => formatExportDate(t.createdAt) },
];

const TicketsPanel = () => {
  const [filters, setFilters] = useState(EMPTY_PARTNER_TICKET_FILTERS);
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const setFilter = (patch) => { setFilters((prev) => ({ ...prev, ...patch })); setPage(1); };
  const filtersActive = Object.keys(EMPTY_PARTNER_TICKET_FILTERS).some((k) => filters[k]);

  const statsQuery = useQuery({
    queryKey: ['superadmin-partner-ticket-stats'],
    queryFn: async () => (await partnerTicketAdminAPI.getTicketStats()).data?.data || {},
  });
  const stats = statsQuery.data || {};

  const ticketsQuery = useQuery({
    queryKey: ['superadmin-partner-tickets', filters, page, limit],
    queryFn: async () => (await partnerTicketAdminAPI.getTickets({ ...partnerTicketApiParams(filters), page, limit })).data,
  });
  const tickets = ticketsQuery.data?.data || [];
  const pagination = ticketsQuery.data?.pagination || {};

  // Filter dropdown options (best-effort)
  const stagesQuery = useQuery({
    queryKey: ['superadmin-partner-ticket-stages'],
    queryFn: async () => (await partnerTicketAdminAPI.getStages()).data?.data || [],
    retry: false,
  });
  const categoriesQuery = useQuery({
    queryKey: ['superadmin-partner-ticket-categories'],
    queryFn: async () => (await partnerTicketAdminAPI.getCategories()).data?.data || [],
    retry: false,
  });
  const assigneesQuery = useQuery({
    queryKey: ['superadmin-assignable-admins'],
    queryFn: async () => (await superAdminAPI.getAssignableSupportAdmins()).data?.data || [],
    retry: false,
  });
  const partnersQuery = useQuery({
    queryKey: ['superadmin-channel-partner-options'],
    queryFn: async () => (await channelPartnerAdminAPI.getAll({ limit: 500 })).data?.data || [],
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const handleSearch = (e) => {
    e.preventDefault();
    setFilter({ search: searchInput.trim() });
  };

  const statCards = [
    { label: 'Total Tickets', value: stats.total, icon: HiOutlineTicket, color: 'text-primary-600 bg-primary-50' },
    { label: 'Open', value: stats.open, icon: HiOutlineClock, color: 'text-blue-600 bg-blue-50' },
    { label: 'Urgent', value: stats.urgent, icon: HiOutlineExclamationCircle, color: 'text-red-600 bg-red-50' },
    { label: 'Resolved', value: stats.resolved, icon: HiOutlineCheckCircle, color: 'text-green-600 bg-green-50' },
  ];

  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <div key={card.label} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{card.label}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{statsQuery.isLoading ? '…' : card.value ?? 0}</p>
                </div>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${card.color}`}>
                  <Icon className="w-5 h-5" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <form onSubmit={handleSearch} className="relative flex-1">
            <HiOutlineSearch className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
              placeholder="Search by ticket #, subject, partner name, email or phone… (press Enter)"
            />
          </form>
          <ExportMenu
            fileName="partner-tickets"
            columns={PARTNER_TICKET_EXPORT_COLUMNS}
            getRows={() => fetchAllPages(partnerTicketAdminAPI.getTickets, partnerTicketApiParams(filters))}
          />
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <FilterSelect
            value={filters.priority}
            onChange={(v) => setFilter({ priority: v })}
            allLabel="All Priorities"
            options={[['low', 'Low'], ['medium', 'Medium'], ['high', 'High'], ['urgent', 'Urgent']]}
          />
          <FilterSelect
            value={filters.state}
            onChange={(v) => setFilter({ state: v })}
            allLabel="All States"
            options={[['open', 'Open'], ['resolved', 'Resolved'], ['closed', 'Closed']]}
          />
          <FilterSelect
            value={filters.stage}
            onChange={(v) => setFilter({ stage: v })}
            allLabel="All Stages"
            options={(stagesQuery.data || []).map((s) => [s._id, s.name])}
          />
          <FilterSelect
            value={filters.category}
            onChange={(v) => setFilter({ category: v })}
            allLabel="All Categories"
            options={(categoriesQuery.data || []).map((c) => [c._id, c.name])}
          />
          <FilterSelect
            value={filters.assignee}
            onChange={(v) => setFilter({ assignee: v })}
            allLabel="All Assignees"
            options={[['unassigned', 'Unassigned'], ...(assigneesQuery.data || []).map((a) => [a._id, a.name])]}
          />
          {(partnersQuery.data || []).length > 0 && (
            <FilterSelect
              value={filters.channelPartnerId}
              onChange={(v) => setFilter({ channelPartnerId: v })}
              allLabel="All Channel Partners"
              options={partnersQuery.data.map((p) => [p._id, p.name])}
              className="max-w-[220px]"
            />
          )}
          <DateRangeFilter
            label="Created"
            from={filters.from}
            to={filters.to}
            onChange={({ from, to }) => setFilter({ from, to })}
          />
          <ResetFiltersButton
            active={filtersActive}
            onReset={() => { setSearchInput(''); setFilter(EMPTY_PARTNER_TICKET_FILTERS); }}
          />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {ticketsQuery.isLoading ? (
          <div className="flex items-center justify-center py-16"><Spinner /></div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-16">
            <HiOutlineTicket className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No tickets found</p>
            <p className="text-sm text-gray-400 mt-1">{filtersActive ? 'Try changing the filters' : 'Tickets raised by channel partners will appear here'}</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {tickets.map((t) => (
              <button key={t._id} onClick={() => setSelectedTicketId(t._id)} className="w-full text-left px-4 py-3.5 hover:bg-gray-50 transition-colors flex items-center gap-4">
                <div className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                  {initials(t.customerName || t.channelPartnerId?.name)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-mono text-gray-500">{t.ticketNumber}</span>
                    <span className="text-sm font-medium text-gray-900 truncate">{t.subject}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-0.5">
                    <span className="truncate">{t.channelPartnerId?.name || '—'}</span>
                    <span className="text-gray-300">•</span>
                    <span className="whitespace-nowrap">{formatDate(t.createdAt)}</span>
                    {t.assignedSuperAdminIds?.length > 0 && (
                      <>
                        <span className="text-gray-300">•</span>
                        <span className="truncate" title="Assigned support staff">{t.assignedSuperAdminIds.map((a) => a.name).join(', ')}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-xs px-2 py-1 rounded font-medium capitalize ${PRIORITY_STYLES[t.priority] || PRIORITY_STYLES.medium}`}>{t.priority}</span>
                  {t.stage && <span className="text-xs px-2 py-1 rounded font-medium text-white" style={{ backgroundColor: t.stage.color || '#6B7280' }}>{t.stage.name}</span>}
                  <HiOutlineChevronRight className="w-4 h-4 text-gray-300" />
                </div>
              </button>
            ))}
          </div>
        )}

        {pagination.total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <span className="text-xs text-gray-500">Page {pagination.page} of {pagination.totalPages} · {pagination.total} total</span>
            <div className="flex gap-2">
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg disabled:opacity-50">Previous</button>
              <button disabled={page >= pagination.totalPages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg disabled:opacity-50">Next</button>
            </div>
          </div>
        )}
      </div>

      {selectedTicketId && <TicketDetailDrawer ticketId={selectedTicketId} onClose={() => setSelectedTicketId(null)} />}
    </>
  );
};

// ─── Categories Panel ───────────────────────────────────────────────────────

const CategoriesPanel = () => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(null); // category being edited, or {} for new
  const [form, setForm] = useState({ name: '', description: '' });

  const { data, isLoading } = useQuery({
    queryKey: ['superadmin-partner-ticket-categories'],
    queryFn: async () => (await partnerTicketAdminAPI.getCategories()).data,
  });
  const categories = data?.data || [];

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['superadmin-partner-ticket-categories'] });

  const saveMutation = useMutation({
    mutationFn: () => (editing._id ? partnerTicketAdminAPI.updateCategory(editing._id, form) : partnerTicketAdminAPI.createCategory(form)),
    onSuccess: () => { invalidate(); toast.success('Category saved'); setEditing(null); },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to save category'),
  });
  const toggleMutation = useMutation({
    mutationFn: (id) => partnerTicketAdminAPI.toggleCategory(id),
    onSuccess: invalidate,
  });
  const deleteMutation = useMutation({
    mutationFn: (id) => partnerTicketAdminAPI.deleteCategory(id),
    onSuccess: () => { invalidate(); toast.success('Category deleted'); },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to delete category'),
  });

  const openNew = () => { setEditing({}); setForm({ name: '', description: '' }); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, description: c.description || '' }); };

  return (
    <div className="bg-white rounded-xl border border-gray-200">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <h3 className="font-semibold text-gray-900">Categories</h3>
        <button onClick={openNew} className="flex items-center gap-1.5 bg-primary-600 text-white rounded-lg px-3 py-1.5 text-sm font-medium hover:bg-primary-700">
          <HiOutlinePlus className="w-4 h-4" /> Add Category
        </button>
      </div>
      {isLoading ? (
        <div className="flex items-center justify-center py-12"><Spinner /></div>
      ) : (
        <div className="divide-y divide-gray-100">
          {categories.map((c) => (
            <div key={c._id} className="flex items-center justify-between px-4 py-3">
              <div>
                <p className={`text-sm font-medium ${c.isActive ? 'text-gray-900' : 'text-gray-400'}`}>{c.name}</p>
                {c.description && <p className="text-xs text-gray-400">{c.description}</p>}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => toggleMutation.mutate(c._id)} className={`text-xs px-2 py-1 rounded-lg ${c.isActive ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {c.isActive ? 'Active' : 'Inactive'}
                </button>
                <button onClick={() => openEdit(c)} className="p-1.5 rounded-lg hover:bg-gray-100"><HiOutlinePencil className="w-4 h-4 text-gray-500" /></button>
                <button onClick={() => deleteMutation.mutate(c._id)} className="p-1.5 rounded-lg hover:bg-red-50"><HiOutlineTrash className="w-4 h-4 text-red-500" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/40" onClick={() => setEditing(null)} />
          <div className="relative bg-white rounded-xl p-5 w-full max-w-sm space-y-3">
            <h4 className="font-semibold text-gray-900">{editing._id ? 'Edit Category' : 'New Category'}</h4>
            <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            <input type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description (optional)" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            <div className="flex gap-2">
              <button onClick={() => setEditing(null)} className="flex-1 border border-gray-300 rounded-lg py-2 text-sm">Cancel</button>
              <button onClick={() => saveMutation.mutate()} disabled={!form.name.trim() || saveMutation.isPending} className="flex-1 bg-primary-600 text-white rounded-lg py-2 text-sm disabled:opacity-60">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Stages Panel ───────────────────────────────────────────────────────────

const StagesPanel = () => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', color: '#6B7280', isResolved: false, isClosed: false });

  const { data, isLoading } = useQuery({
    queryKey: ['superadmin-partner-ticket-stages'],
    queryFn: async () => (await partnerTicketAdminAPI.getStages()).data,
  });
  const stages = data?.data || [];

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['superadmin-partner-ticket-stages'] });

  const saveMutation = useMutation({
    mutationFn: () => (editing._id ? partnerTicketAdminAPI.updateStage(editing._id, form) : partnerTicketAdminAPI.createStage(form)),
    onSuccess: () => { invalidate(); toast.success('Stage saved'); setEditing(null); },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to save stage'),
  });
  const toggleMutation = useMutation({ mutationFn: (id) => partnerTicketAdminAPI.toggleStage(id), onSuccess: invalidate });
  const deleteMutation = useMutation({
    mutationFn: (id) => partnerTicketAdminAPI.deleteStage(id),
    onSuccess: () => { invalidate(); toast.success('Stage deleted'); },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to delete stage'),
  });
  const reorderMutation = useMutation({
    mutationFn: (order) => partnerTicketAdminAPI.reorderStages(order),
    onSuccess: invalidate,
  });

  const move = (index, dir) => {
    const next = [...stages];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    reorderMutation.mutate(next.map((s) => s._id));
  };

  const openNew = () => { setEditing({}); setForm({ name: '', color: '#6B7280', isResolved: false, isClosed: false }); };
  const openEdit = (s) => { setEditing(s); setForm({ name: s.name, color: s.color, isResolved: s.isResolved, isClosed: s.isClosed }); };

  return (
    <div className="bg-white rounded-xl border border-gray-200">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <h3 className="font-semibold text-gray-900">Workflow Stages</h3>
        <button onClick={openNew} className="flex items-center gap-1.5 bg-primary-600 text-white rounded-lg px-3 py-1.5 text-sm font-medium hover:bg-primary-700">
          <HiOutlinePlus className="w-4 h-4" /> Add Stage
        </button>
      </div>
      {isLoading ? (
        <div className="flex items-center justify-center py-12"><Spinner /></div>
      ) : (
        <div className="divide-y divide-gray-100">
          {stages.map((s, i) => (
            <div key={s._id} className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-3">
                <div className="flex flex-col">
                  <button disabled={i === 0} onClick={() => move(i, -1)} className="disabled:opacity-30"><HiOutlineArrowUp className="w-3.5 h-3.5 text-gray-400" /></button>
                  <button disabled={i === stages.length - 1} onClick={() => move(i, 1)} className="disabled:opacity-30"><HiOutlineArrowDown className="w-3.5 h-3.5 text-gray-400" /></button>
                </div>
                <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
                <div>
                  <p className={`text-sm font-medium ${s.isActive ? 'text-gray-900' : 'text-gray-400'}`}>{s.name}</p>
                  <p className="text-xs text-gray-400">{s.isResolved && 'Resolved · '}{s.isClosed && 'Closed'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => toggleMutation.mutate(s._id)} className={`text-xs px-2 py-1 rounded-lg ${s.isActive ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {s.isActive ? 'Active' : 'Inactive'}
                </button>
                <button onClick={() => openEdit(s)} className="p-1.5 rounded-lg hover:bg-gray-100"><HiOutlinePencil className="w-4 h-4 text-gray-500" /></button>
                <button onClick={() => deleteMutation.mutate(s._id)} className="p-1.5 rounded-lg hover:bg-red-50"><HiOutlineTrash className="w-4 h-4 text-red-500" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/40" onClick={() => setEditing(null)} />
          <div className="relative bg-white rounded-xl p-5 w-full max-w-sm space-y-3">
            <h4 className="font-semibold text-gray-900">{editing._id ? 'Edit Stage' : 'New Stage'}</h4>
            <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500">Color</label>
              <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="w-10 h-8 rounded" />
            </div>
            <label className="flex items-center gap-1.5 text-xs text-gray-600"><input type="checkbox" checked={form.isResolved} onChange={(e) => setForm({ ...form, isResolved: e.target.checked })} /> Counts as resolved</label>
            <label className="flex items-center gap-1.5 text-xs text-gray-600"><input type="checkbox" checked={form.isClosed} onChange={(e) => setForm({ ...form, isClosed: e.target.checked })} /> Counts as closed</label>
            <div className="flex gap-2">
              <button onClick={() => setEditing(null)} className="flex-1 border border-gray-300 rounded-lg py-2 text-sm">Cancel</button>
              <button onClick={() => saveMutation.mutate()} disabled={!form.name.trim() || saveMutation.isPending} className="flex-1 bg-primary-600 text-white rounded-lg py-2 text-sm disabled:opacity-60">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main Page ──────────────────────────────────────────────────────────────

const TABS = [
  { key: 'tickets', label: 'Tickets' },
  { key: 'categories', label: 'Categories' },
  { key: 'stages', label: 'Workflow Stages' },
];

const PartnerTickets = () => {
  const [activeTab, setActiveTab] = useState('tickets');

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Partner Tickets</h1>
          <p className="text-sm text-gray-500 mt-1">Support tickets raised by channel partners — a separate system from the org Support tab</p>
        </div>
      </div>

      <div className="flex gap-1 border-b border-gray-200 mb-5">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === t.key ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'tickets' && <TicketsPanel />}
      {activeTab === 'categories' && <CategoriesPanel />}
      {activeTab === 'stages' && <StagesPanel />}
    </div>
  );
};

export default PartnerTickets;
